import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import {
    ActionsColumn,
    BalanceColumn,
    EmailColumn,
    UsernameColumn,
} from './columns';
import {
    Box,
    Container,
    Flex,
    Group,
    MultiSelect,
    Portal,
    Select,
    Text,
} from '@mantine/core';
import { DateTimeColumn, MultiSelectCreatable } from '../../../common';
import {
    getDatasets,
    getMessageGroups,
    getUsers,
} from '../../../../http/apiClient';
import {
    useDebouncedCallback,
    useDisclosure,
    useLocalStorage,
} from '@mantine/hooks';
import { useMemo, useState } from 'react';

import AddBalanceModal from '../../../modals/billing/AddBalanceModal';
import CheckboxColumn from '../../../common/tables/columns/CheckboxColumn';
import FilterTopToolbar from '../../../common/tables/toolbars/FilterTopToolbar';
import FiltersMultiSelect from '../../../common/filtersMultiSelect/FiltersMultiSelect';
import { MantineReactTable } from 'mantine-react-table';
import UserBillingModal from '../../../modals/billing/UserBillingModal';
import { usePaginatedQuery } from '../../../../http/queries/usePaginatedQuery';
import useToggleUserProperty from '../../../../http/mutations/users/useToggleUserProperty';

const AdminUsersView = ({ ...props }) => {
    const [globalFilter, setGlobalFilter] = useState(null);
    const [activeFilters, setActiveFilters] = useState([]);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const [modalUser, setModalUser] = useState();
    const [
        balanceModalOpen,
        { open: openBalanceModal, close: closeBalanceModal },
    ] = useDisclosure();

    const [
        billingModalOpen,
        { open: openBillingModal, close: closeBillingModal },
    ] = useDisclosure();

    const handleOpenBalanceModal = (user) => {
        setModalUser(user);
        openBalanceModal();
    };

    const handleOpenBillingModal = (user) => {
        setModalUser(user);
        openBillingModal();
    };

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) => getUsers(queryParams),
        queryKey: ['users'],
        refetchInterval: 15_0000,
        globalFilter,
        pagination,
        sorting,
    });

    const { data: users, pagination: dataPagination } = paginatedData || {};

    const { mutate: setActive } = useToggleUserProperty({
        property: 'active',
    });
    const { mutate: setBanned } = useToggleUserProperty({
        property: 'banned',
    });

    const handleActiveChange = (rowData, value) => {
        setActive({
            uuid: rowData.id,
            value: value,
        });
    };

    const handleBannedChange = (rowData, value) => {
        setBanned({
            id: rowData.id,
            value: value,
        });
    };

    const columns = useMemo(
        () => [
            UsernameColumn(),
            EmailColumn(),
            BalanceColumn({
                openBalanceModal: handleOpenBalanceModal,
                openBillingModal: handleOpenBillingModal,
            }),
            CheckboxColumn({
                id: 'active',
                accessorKey: 'active',
                header: 'Active',
                onChange: handleActiveChange,
            }),
            CheckboxColumn({
                id: 'banned',
                accessorKey: 'banned',
                header: 'Banned',
                onChange: handleBannedChange,
            }),
            DateTimeColumn({
                id: 'lastLogin',
                header: 'Last Login',
                accessorFn: (rowData) => rowData.lastLogin,
                format: 'L LTS',
            }),
            DateTimeColumn({
                id: 'createdAt',
                header: 'Created At',
                accessorFn: (rowData) => rowData.createdAt,
                format: 'L LTS',
            }),
        ],
        [users]
    );

    /*const tableData = useMemo(
        () => (isLoading ? [] : campaigns),
        [campaigns, isLoading]
    );*/

    const tableData = useMemo(
        () => (isLoading ? [] : users),
        [users, isLoading]
    );

    const [columnVisibility, setColumnVisibility] = useLocalStorage({
        key: 'datasets-table-visibility', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const [columnSizing, setColumnSizing] = useLocalStorage({
        key: 'datasets-group-table-sizing', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const handleVisibilityChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnVisibility((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handlSizeChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnSizing((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handleFilterChange = (queryStr) => {
        filterDebounce(queryStr);
    };

    const tableProps = {
        columns: columns,
        data: tableData,
        enableRowActions: true,
        enableColumnResizing: true,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: true,
        enableGlobalFilter: true,
        enableFilters: false,
        enableStickyHeader: true,
        enableDensityToggle: false,
        positionActionsColumn: 'last',
        layoutMode: 'semantic',
        mantineSearchTextInputProps: {
            placeholder: 'Search Users',
            miw: 200,
        },
        mantineTableContainerProps: {
            style: {
                minHeight: '80vh',
            },
        },
        positionGlobalFilter: 'left',
        renderTopToolbarCustomActions: () => (
            <FilterTopToolbar
                activeFilters={activeFilters}
                filterOptions={[
                    {
                        group: 'Status',
                        items: [
                            { label: 'Active', value: 'active' },
                            { label: 'Banned', value: 'banned' },
                        ],
                    },
                ]}
                onFilterChange={setActiveFilters}
            />
        ),
        renderRowActions: ActionsColumn,

        manualPagination: true,
        manualSorting: true,
        onColumnVisibilityChange: handleVisibilityChange,
        onColumnSizingChange: handlSizeChange,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: handleFilterChange,
        rowCount: dataPagination?.totalItems || 0,
        pageCount: dataPagination?.totalPages || 1,

        initialState: {
            columnVisibility,
            pagination,
            isLoading,
            showGlobalFilter: true,
        },
        state: {
            isLoading,
            pagination,
            columnVisibility,
            columnSizing,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            sorting,
            density: 'xs',
        },
    };

    return (
        <Container fluid>
            <MantineReactTable {...tableProps} />
            <AddBalanceModal
                user={modalUser}
                opened={balanceModalOpen}
                close={closeBalanceModal}
            />
            <UserBillingModal
                user={modalUser}
                opened={billingModalOpen}
                close={closeBillingModal}
            />
        </Container>
    );
};
export default AdminUsersView;
